<template>
  <div>
    <v-alert v-if="error" type="error" variant="tonal" class="mb-3">
      {{ error }}
    </v-alert>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />

    <div v-if="jsonOnly" class="mb-4">
      <div v-if="rawJson" class="publish-logs-viewer">
        <div class="publish-logs-scroll">
          <div class="publish-logs-json-pane">
            <v-tooltip text="Copy JSON" location="left">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  class="publish-logs-copy-fab"
                  icon="mdi-content-copy"
                  size="small"
                  variant="tonal"
                  color="primary"
                  :loading="isCopying"
                  :disabled="!rawJson || isCopying"
                  aria-label="Copy JSON"
                  data-testid="publish-logs-copy-json"
                  @click="copy"
                />
              </template>
            </v-tooltip>

            <pre class="debug-pre" data-testid="publish-logs-json-pre">{{ formattedRawJson }}</pre>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="logs.length > 0" class="mb-4">
      <div class="d-flex align-center justify-space-between flex-wrap ga-3 mb-3">
        <div class="text-subtitle-2 font-weight-medium">{{ title }}</div>

        <v-btn-toggle
          v-if="showDebugPanels"
          v-model="activeView"
          mandatory
          density="comfortable"
          variant="outlined"
          color="primary"
          divided
        >
          <v-btn value="timeline" size="small" prepend-icon="mdi-timeline-text-outline">
            Timeline
          </v-btn>
          <v-btn value="json" size="small" prepend-icon="mdi-code-json">
            JSON
          </v-btn>
        </v-btn-toggle>
      </div>

      <div class="publish-logs-viewer">
        <v-window v-model="activeView" class="publish-logs-window">
          <v-window-item value="timeline">
            <div class="publish-logs-scroll">
              <v-timeline side="end" density="compact" class="mb-4 publish-logs-timeline" align="start">
                <v-timeline-item
                  v-for="(log, idx) in logs"
                  :key="idx"
                  :dot-color="log.color"
                  :icon="log.icon"
                  size="small"
                >
                  <div class="d-flex flex-column publish-logs-item-content">
                    <DateTimeWithDealerTooltip
                      :value="log.tsRaw"
                      kind="time"
                      :text="log.timestamp"
                      text-class="text-caption text-grey-darken-1"
                    />
                    <div class="d-flex align-center flex-wrap ga-2 mt-1">
                      <div class="text-body-2 publish-logs-activity">
                        {{ log.message }}
                      </div>
                      <v-chip size="x-small" color="grey" variant="tonal" class="publish-logs-source-chip">
                        {{ log.source }}
                      </v-chip>
                    </div>
                    <div v-if="log.metrics && log.metrics.length > 0" class="d-flex flex-wrap ga-3 mt-2 ml-2">
                      <v-chip
                        v-for="(metric, midx) in log.metrics"
                        :key="midx"
                        size="x-small"
                        :color="metric.color"
                        variant="flat"
                      >
                        {{ metric.label }}: {{ metric.value }}
                      </v-chip>
                    </div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </div>
          </v-window-item>

          <v-window-item v-if="showDebugPanels" value="json">
            <div class="publish-logs-scroll">
              <div class="publish-logs-json-pane">
                <v-tooltip text="Copy JSON" location="left">
                  <template #activator="{ props: tooltipProps }">
                    <v-btn
                      v-bind="tooltipProps"
                      class="publish-logs-copy-fab"
                      icon="mdi-content-copy"
                      size="small"
                      variant="tonal"
                      color="primary"
                      :loading="isCopying"
                      :disabled="!rawJson || isCopying"
                      aria-label="Copy JSON"
                      @click="copy"
                    />
                  </template>
                </v-tooltip>

                <pre class="debug-pre">{{ formattedRawJson }}</pre>
              </div>
            </div>
          </v-window-item>
        </v-window>
      </div>
    </div>

    <pre v-else-if="rawJson" class="debug-pre">{{ formattedRawJson }}</pre>

    <v-snackbar v-model="snack.show" :color="snack.color" timeout="2000">
      {{ snack.message }}
    </v-snackbar>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import type { PublishHumanLog } from '@/utils/publishLogsTimeline'
import DateTimeWithDealerTooltip from '@/components/DateTimeWithDealerTooltip.vue'

const props = withDefaults(
  defineProps<{
    logs: PublishHumanLog[]
    rawJson: string
    loading?: boolean
    error?: string
    showDebugPanels?: boolean
    /** When true, render only the JSON/raw view (no timeline tab or visual steps). */
    jsonOnly?: boolean
    title?: string
  }>(),
  {
    loading: false,
    error: '',
    showDebugPanels: false,
    jsonOnly: false,
    title: 'Activity Timeline',
  },
)

const activeView = ref<'timeline' | 'json'>('timeline')
const isCopying = ref(false)
const snack = ref<{ show: boolean; message: string; color: 'success' | 'error' }>({
  show: false,
  message: '',
  color: 'success',
})

const formattedRawJson = computed(() => formatDebugJson(props.rawJson))

function formatDebugJson(value: string): string {
  const raw = String(value || '').trim()
  if (!raw) return ''

  // Whole response is valid JSON.
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    // Fall through to JSONL handling.
  }

  // Logs are often newline-delimited JSON. Pretty-print each line.
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)

  if (lines.length === 0) return raw

  const formattedLines: string[] = []
  let parsedAnyLine = false

  for (const line of lines) {
    try {
      formattedLines.push(JSON.stringify(JSON.parse(line), null, 2))
      parsedAnyLine = true
    } catch {
      formattedLines.push(line)
    }
  }

  return parsedAnyLine ? formattedLines.join('\n\n') : raw
}

const copy = async () => {
  isCopying.value = true
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(formattedRawJson.value)
    } else {
      const el = document.createElement('textarea')
      el.value = formattedRawJson.value
      el.style.position = 'fixed'
      el.style.left = '-9999px'
      el.setAttribute('readonly', 'true')
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    }
    snack.value = { show: true, message: 'Publish logs JSON copied to clipboard.', color: 'success' }
  } catch (e) {
    console.warn('Failed to copy publish logs JSON', e)
    snack.value = { show: true, message: 'Failed to copy JSON. Please select and copy manually.', color: 'error' }
  } finally {
    isCopying.value = false
  }
}
</script>

<style scoped>
.publish-logs-viewer {
  height: min(560px, 65vh);
  max-width: 100%;
  overflow: hidden;
}

.publish-logs-window {
  height: 100%;
}

.publish-logs-window :deep(.v-window__container),
.publish-logs-window :deep(.v-window-item) {
  height: 100%;
}

.publish-logs-scroll {
  height: 100%;
  max-width: 100%;
  overflow: auto;
  padding-right: 4px;
}

.publish-logs-json-pane {
  position: relative;
  min-height: 100%;
}

.publish-logs-copy-fab {
  position: sticky;
  top: 8px;
  float: right;
  z-index: 3;
  margin: 8px 8px 8px 12px;
  opacity: 0.92;
  backdrop-filter: blur(4px);
}

.publish-logs-copy-fab:hover,
.publish-logs-copy-fab:focus-visible {
  opacity: 1;
}

.debug-pre {
  max-width: 100%;
  min-height: 100%;
  margin: 0;
  padding: 12px;
  padding-right: 48px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.publish-logs-source-chip {
  opacity: 0.9;
}

.publish-logs-timeline {
  max-width: 100%;
}

.publish-logs-item-content {
  min-width: 0;
  max-width: 100%;
}

.publish-logs-activity {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
}
</style>

